import React, { useMemo } from 'react'
import Loader from 'components/Shared/Loader'
import { theme } from 'theme'
import { ButtonContainer } from './styles'
import { CSSProp } from 'particles/themes/styled'

type ButtonSize = 's' | 'm' | 'l'
type ButtonType = 'primary' | 'secondary' | 'tertiary' | 'filled' | 'outlined' | 'text'

interface ButtonProps {
    text: string
    size?: ButtonSize
    buttonType: ButtonType
    onClick: (e: React.MouseEvent<HTMLButtonElement>) => void
    customStyle?: CSSProp
    icon?: boolean
    children?: React.ReactChild
    iconRight?: boolean
    isDisable?: boolean
    isLoading?: boolean
    isActive?: boolean
    id?: string
    className?: string
    type?: 'button' | 'submit' | 'reset'
    href?: string
    loadingText?: string
}

const buttonSizeMap = {
    s: { minWidth: '84px', height: '28px', padding: '8px', fontSize: theme.newFontSizes.s },
    m: { minWidth: '102px', height: '32px', padding: '10px 16px', fontSize: theme.newFontSizes.s },
    l: { minWidth: '120px', height: '40px', padding: '12px 16px', fontSize: theme.newFontSizes.s },
}

const Button: React.FC<ButtonProps> = ({
    type,
    text,
    size = 'm',
    customStyle,
    icon,
    children,
    buttonType,
    iconRight = false,
    isActive = false,
    isDisable = false,
    isLoading,
    onClick,
    id,
    className,
    href,
    loadingText,
}) => {
    const buttonSize = useMemo(() => buttonSizeMap[size], [size])

    const isLinkButton = Boolean(href)

    return (
        <ButtonContainer
            as={isLinkButton ? 'a' : 'button'}
            href={isLinkButton ? href : undefined}
            onContextMenu={(e: React.MouseEvent<HTMLButtonElement>): void =>
                isDisable ? e.preventDefault() : undefined
            }
            type={type}
            minWidth={buttonSize.minWidth}
            height={buttonSize.height}
            padding={buttonSize.padding}
            fontSize={buttonSize.fontSize}
            reverseDisplay={iconRight}
            onClick={(e: React.MouseEvent<HTMLButtonElement>): void => {
                if ((e.metaKey || e.ctrlKey) && !isDisable) return // Allow command-click and control-click to open new tab
                e.preventDefault()
                if (!isLoading && !isDisable) onClick(e)
            }}
            isActive={isActive}
            customStyle={customStyle}
            className={`${buttonType} ${isDisable && 'disable'} ${className ? className : ''} customStyle`}
            id={id}
            isDisable={isDisable}
            isIcon={children !== undefined}
        >
            {isLoading ? (
                <Loader
                    size="s"
                    progressBackgroundColor={buttonType !== 'primary' ? theme.newColors.neutral200 : undefined}
                    progressBarColor={buttonType !== 'primary' ? theme.newColors.neutral500 : undefined}
                    loadingText={loadingText}
                />
            ) : (
                <>
                    {icon && children}
                    <div className="button-text">{text}</div>
                </>
            )}
        </ButtonContainer>
    )
}
/**
 * @deprecated Please use particles/Button
 */
export default Button
